import { useState } from 'react';

interface DeletePlanConfirmModalProps {
    isOpen: boolean;
    planName: string;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}

/**
 * Clear warning confirmation modal for deleting a training plan.
 * Requires the user to type the plan name to confirm deletion.
 */
export const DeletePlanConfirmModal = ({ isOpen, planName, onClose, onConfirm }: DeletePlanConfirmModalProps) => {
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    if (!isOpen) return null;

    const isConfirmed = confirmText.trim().toLowerCase() === planName.trim().toLowerCase();

    const handleDelete = async () => {
        if (!isConfirmed) return;
        setIsDeleting(true);
        try {
            await onConfirm();
            onClose();
        } catch (err) {
            console.error('Delete failed:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="dtp-overlay" onClick={onClose}>
            <div className="dtp-popup" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                <div style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <span style={{ fontSize: 28 }}>⚠️</span>
                        <h3 style={{ margin: 0, color: '#dc2626' }}>Delete Training Plan</h3>
                    </div>

                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 14, marginBottom: 16 }}>
                        <p style={{ margin: '0 0 8px', fontSize: 14, color: '#991b1b', fontWeight: 600 }}>
                            This action cannot be undone.
                        </p>
                        <p style={{ margin: 0, fontSize: 13, color: '#7f1d1d', lineHeight: 1.5 }}>
                            Deleting this plan will permanently remove:
                        </p>
                        <ul style={{ margin: '8px 0 0', padding: '0 0 0 18px', fontSize: 13, color: '#7f1d1d', lineHeight: 1.8 }}>
                            <li>The plan configuration and categories</li>
                            <li>All scheduled topic assignments</li>
                            <li>All enrollment records for this plan</li>
                        </ul>
                        <p style={{ margin: '10px 0 0', fontSize: 13, color: '#475569', fontStyle: 'italic' }}>
                            Training topics will NOT be deleted — they remain in your Master Topic List.
                        </p>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', fontSize: 13, color: '#475569', marginBottom: 6 }}>
                            Type <strong>"{planName}"</strong> to confirm:
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={e => setConfirmText(e.target.value)}
                            placeholder={planName}
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: 6,
                                fontSize: 14,
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '10px 20px',
                                border: '2px solid #d1d5db',
                                borderRadius: 6,
                                background: '#f9fafb',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#374151',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={!isConfirmed || isDeleting}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: 6,
                                background: isConfirmed ? '#dc2626' : '#e5e7eb',
                                color: isConfirmed ? '#fff' : '#9ca3af',
                                cursor: isConfirmed ? 'pointer' : 'not-allowed',
                                fontSize: 13,
                                fontWeight: 600,
                            }}
                        >
                            {isDeleting ? 'Deleting...' : '🗑 Delete Plan Permanently'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
